/**
 * BUSCA, ORDENAÇÃO, RECORTE e RESUMO da lista de CONTRATOS (`/contratos`).
 *
 * Vive fora da página pelo mesmo motivo do `quoteListFilters`, do
 * `serviceOrdersListFilters` e do `receiptsListFilters`: são regras que decidem
 * QUAIS contratos o prestador vê e em que ordem, e todas erram em silêncio —
 * uma lista recortada errado não dá erro, só esconde documento.
 *
 * ─── O QUE É ESPECÍFICO DESTA LISTA ─────────────────────────────────────────
 *
 * · **`cancelled` é o status MORTO daqui, e `draft` NÃO é.** O hero soma
 *   dinheiro: um contrato cancelado é negócio que não vai acontecer e somá-lo
 *   diria ao prestador que ele tem mais fechado do que tem (o corolário do
 *   #182/#188). Um RASCUNHO, ao contrário, é dinheiro que ainda pode entrar —
 *   é o análogo do orçamento `pending`, e tirá-lo da soma faria a tela esconder
 *   justamente o que está por fechar. Quantos ficaram fora é DEVOLVIDO para a
 *   tela dizer: "no silent caps" (#61).
 *
 * · **O status pode ser NULO, e o default é `draft`.** É o mesmo default que o
 *   card e o `isContentLocked` já usam (`status ?? 'draft'`). Tratar a ausência
 *   como "sem status" esconderia a linha do único botão que deveria trazê-la.
 *
 * · **A busca por `#N` é EXATA e EXCLUSIVA**, igual à de `/quotes` (#190c) e à
 *   de `/ordens-servico`: `contracts.sequence_id` é inteiro exibido com 4 casas,
 *   então casar como texto faria "#7" trazer #0017 e #0070 e a busca leria como
 *   aleatória. E quem digita "#12" quer o documento 12, não todo cliente com
 *   "12" no nome — por isso o `#` desliga o casamento textual. O parser é o
 *   MESMO de `/quotes` (`parseSequenceQuery`): duas cópias divergiriam na
 *   primeira correção.
 *
 * · **A data do recorte é `created_at` (TIMESTAMP), não `client_signed_at`.** As
 *   duas existem e significam coisas diferentes ("quando eu criei" × "quando o
 *   cliente assinou"); o filtro de período responde à primeira, que é o mesmo
 *   contrato de `/quotes`, `/trabalhos` e `/ordens-servico`. Recortar pela
 *   assinatura faria TODO contrato não assinado sumir do período, sem
 *   explicação nenhuma — e é justamente o não assinado que se vai procurar.
 *
 * O parser de dinheiro é o `quoteTotalNumber` (#123a) — `total` chega como
 * `number` do formulário e como STRING do PostgREST (`numeric`) — e a
 * normalização de texto dobra caixa E acento (#190d), senão quem digita
 * "manutencao" no teclado do celular recebe zero por "Manutenção".
 */
import type { ContractStatus } from '@/hooks/useContracts';
import { normalizeSearchText, parseSequenceQuery } from '@/lib/quoteSearch';
import { quoteTotalNumber } from '@/lib/quotesListSummary';
import { matchesListRange, type ListValueRange } from '@/lib/listFilters';
import type { ReportDateRange, ReportPeriodPreset } from '@/lib/reportPeriod';

/**
 * O que este módulo enxerga de um contrato.
 *
 * As linhas chegam do cache tipadas como `any` (e do PostgREST com `numeric`
 * como string), então tudo aqui é lido com cuidado — nada é `Number()` cru.
 */
export interface ContractRow {
  id?: string | null;
  title?: string | null;
  client_name?: string | null;
  sequence_id?: number | null;
  status?: string | null;
  total?: unknown;
  created_at?: string | null;
  client_signed_at?: string | null;
}

/**
 * A FONTE ÚNICA do "em que status este contrato está".
 *
 * Ausência é `draft` — ver o cabeçalho. Um valor fora do vocabulário é
 * devolvido como veio, para o recorte "Todos" continuar mostrando a linha em
 * vez de escondê-la num balde que nenhum botão representa.
 */
export function contractStatusOf(contract: ContractRow): ContractStatus {
  return (contract?.status ?? 'draft') as ContractStatus;
}

// ─── Busca ───────────────────────────────────────────────────────────────────

/**
 * Termo vazio casa com tudo (é o estado sem filtro).
 *
 * Campos varridos: número, título e nome do cliente — exatamente o que o card
 * mostra. Buscar em campo que a linha não exibe devolveria resultados que o
 * usuário não consegue explicar.
 */
export function contractMatchesSearch(contract: ContractRow, term: string): boolean {
  const needle = normalizeSearchText(term);
  if (!needle) return true;

  const wantedSequence = parseSequenceQuery(term);
  if (wantedSequence !== null && contract?.sequence_id === wantedSequence) return true;
  // O `#` explícito NÃO cai no texto: quem pergunta por número quer o documento
  // daquele número, e devolver os dois misturados é o que faz a busca parecer
  // quebrada (#190c).
  if (term.trim().startsWith('#')) return false;

  if (normalizeSearchText(contract?.title).includes(needle)) return true;
  if (normalizeSearchText(contract?.client_name).includes(needle)) return true;
  return false;
}

// ─── Ordenação ───────────────────────────────────────────────────────────────

export type ContractSortOption = 'recent' | 'oldest' | 'highest' | 'lowest' | 'client' | 'title';

export const CONTRACT_SORT_LABELS: Record<ContractSortOption, string> = {
  recent: 'Mais recentes',
  oldest: 'Mais antigos',
  highest: 'Maior valor',
  lowest: 'Menor valor',
  client: 'Nome do cliente',
  title: 'Título do contrato',
};

export const CONTRACT_SORT_ORDER: readonly ContractSortOption[] = [
  'recent',
  'oldest',
  'highest',
  'lowest',
  'client',
  'title',
];

/**
 * `recent` é o PADRÃO porque é como a lista sempre se comportou (a consulta já
 * devolve `created_at desc`). Trocar o padrão junto com o redesenho mudaria a
 * tela de todo mundo sem ninguém ter pedido.
 */
export const DEFAULT_CONTRACT_SORT: ContractSortOption = 'recent';

/**
 * Data ilegível vai para o FIM em "mais recentes" (e não para o topo, que é onde
 * `NaN → 0` a colocaria em "mais antigos"): contrato sem data é exceção, e
 * exceção não ocupa a primeira linha da lista.
 */
function timeOf(contract: ContractRow): number {
  const parsed = new Date(contract?.created_at ?? '').getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Vazio vai para o FIM (`￿`): um punhado de "sem título" no topo esconde o resto. */
function textOf(value: unknown): string {
  return normalizeSearchText(value) || '￿';
}

function totalOf(contract: ContractRow): number {
  return quoteTotalNumber(contract?.total);
}

/** Não muta a entrada: a lista da tela é derivada da consulta. */
export function sortContractsBy<T extends ContractRow>(
  contracts: readonly T[],
  option: ContractSortOption,
): T[] {
  const rows = [...contracts];
  // Desempate ESTÁVEL por id (#93): sem ele, dois contratos do mesmo dia (ou do
  // mesmo valor — valores redondos repetem muito) trocam de lugar entre renders
  // e a lista "pisca" sem ninguém ter mexido em nada.
  const byId = (a: T, b: T) => String(a?.id).localeCompare(String(b?.id));

  switch (option) {
    case 'oldest':
      return rows.sort((a, b) => timeOf(a) - timeOf(b) || byId(a, b));
    case 'highest':
      return rows.sort((a, b) => totalOf(b) - totalOf(a) || byId(a, b));
    case 'lowest':
      return rows.sort((a, b) => totalOf(a) - totalOf(b) || byId(a, b));
    case 'client':
      return rows.sort(
        (a, b) => textOf(a?.client_name).localeCompare(textOf(b?.client_name)) || byId(a, b),
      );
    case 'title':
      return rows.sort((a, b) => textOf(a?.title).localeCompare(textOf(b?.title)) || byId(a, b));
    case 'recent':
    default:
      return rows.sort((a, b) => timeOf(b) - timeOf(a) || byId(a, b));
  }
}

// ─── Recorte por STATUS (os botões da faixa) ────────────────────────────────

export type ContractStatusFilter = 'all' | ContractStatus;

/**
 * Os botões da faixa, na ordem do ciclo de vida do contrato.
 *
 * ─── SEIS RÓTULOS, E O ARRANJO FOI MEDIDO ──────────────────────────────────
 *
 * Esta é a primeira faixa do app com SEIS botões (as outras têm três a cinco), e
 * em 360px sobram **328px** para os chips e os gaps. Medido por réplica de DOM
 * com a fonte real (chips `text-[11px]` + `px-1.5`, gaps de 4px incluídos):
 *
 *   Todos · Rascunhos · Enviados · Parciais · Assinados · Cancelados → 374,5 ✗
 *   Todos · Rascunho  · Enviado  · Parcial  · Assinado  · Cancelado  → 350,8 ✗
 *   Todos · Rascunho  · Enviado  · Parcial  · Assinado  · Cancel.    → 334,5 ✗
 *
 * (Referência da mesma medição: os CINCO de `/quotes` custam 327,6 e cabem por
 * 0,4px — o que confirma que aqui o problema é a sexta pílula, não o texto.)
 *
 * Ou seja: NÃO CABE em uma linha, o singular não resolve, e nem abreviar o
 * último rótulo resolve — ainda faltariam 6,5px. Aqui a decisão é deixar a
 * faixa ROLAR (o `overflow-x-auto` do `FILTER_CHIP_ROW`), como já acontece em
 * `/catalog` (#195) e na aba "Todos" de `/trabalhos` (#197). O que NÃO se faz é
 * abreviar até caber: seriam DUAS abreviações, e "Parcial" já é a forma curta
 * de "Parcialmente assinado" que o app usa no selo do card — cortar mais
 * inventaria uma segunda palavra para o mesmo estado (#191/#199).
 *
 * O PLURAL fica porque é o que todas as outras faixas usam ("Pendentes",
 * "Concluídos") e porque, se vai rolar de qualquer jeito, os 24px que o
 * singular economizaria não compram uma linha — comprariam só incoerência.
 *
 * Os rótulos NÃO saem de `CONTRACT_STATUSES` (que é singular, e existe para o
 * SELO do card e para o formulário): o mesmo texto nos dois lugares seria uma
 * coincidência, não uma regra, e a primeira vez que um dos dois precisasse
 * mudar arrastaria o outro junto.
 */
export const CONTRACT_STATUS_FILTER_OPTIONS: readonly {
  value: ContractStatusFilter;
  label: string;
}[] = [
  { value: 'all', label: 'Todos' },
  { value: 'draft', label: 'Rascunhos' },
  { value: 'sent', label: 'Enviados' },
  { value: 'partially_signed', label: 'Parciais' },
  { value: 'signed', label: 'Assinados' },
  { value: 'cancelled', label: 'Cancelados' },
];

export function matchesContractStatus(
  contract: ContractRow,
  filter: ContractStatusFilter,
): boolean {
  if (filter === 'all') return true;
  return contractStatusOf(contract) === filter;
}

/**
 * Quantos contratos cada botão traria — contados sobre o recorte JÁ filtrado por
 * busca, período e valor, mas ANTES do status.
 *
 * É a única definição que não mente: contar sobre a lista inteira faria o botão
 * prometer 12 com uma busca ativa que devolve 3, e contar depois do status
 * zeraria todos os outros botões (#193/#196).
 */
export function countContractsByStatus(
  contracts: readonly ContractRow[],
): Record<ContractStatusFilter, number> {
  const counts: Record<ContractStatusFilter, number> = {
    all: contracts.length,
    draft: 0,
    sent: 0,
    partially_signed: 0,
    signed: 0,
    cancelled: 0,
  };
  for (const contract of contracts) {
    const status = contractStatusOf(contract);
    if (status in counts && (status as string) !== 'all') counts[status] += 1;
  }
  return counts;
}

// ─── Período e faixa de valor (o modal) ─────────────────────────────────────

/**
 * Presets oferecidos no modal. Subconjunto EXPLÍCITO dos de `reportPeriod`, o
 * mesmo de `/quotes`, `/ordens-servico` e `/recibos`: os longos de relatório não
 * ajudam a achar um contrato, e `custom` exigiria um calendário dentro do modal.
 */
export const CONTRACT_PERIOD_PRESETS: readonly ReportPeriodPreset[] = [
  'today',
  'thisWeek',
  'thisMonth',
  '30d',
  '3m',
  'thisYear',
];

/**
 * `range` nulo = sem filtro de período. Contrato SEM data legível NÃO é
 * recortado: sumir com ele por causa de um carimbo estranho é pior do que
 * mostrá-lo fora da faixa pedida — o usuário não teria como saber que ele
 * existe (#193-d).
 */
export function matchesContractPeriod(
  contract: ContractRow,
  range: ReportDateRange | null,
): boolean {
  if (!range) return true;
  const time = new Date(contract?.created_at ?? '').getTime();
  if (!Number.isFinite(time)) return true;
  return time >= range.from.getTime() && time <= range.to.getTime();
}

/** `null` em `min`/`max` é SEM LIMITE, nunca zero (ver `listFilters`). */
export function matchesContractValue(contract: ContractRow, range: ListValueRange): boolean {
  return matchesListRange(contract?.total, range);
}

/**
 * Quantos filtros do MODAL estão ativos — vira o selo no ícone.
 *
 * O STATUS fica de fora: ele tem os botões visíveis logo acima, então já se
 * anuncia sozinho. Contá-lo faria o selo dizer "1" com o modal em branco.
 */
export function countActiveContractFilters(
  period: ReportPeriodPreset | null,
  range: ListValueRange,
): number {
  let active = 0;
  if (period) active += 1;
  if (range.min !== null || range.max !== null) active += 1;
  return active;
}

// ─── Resumo do hero ─────────────────────────────────────────────────────────

/** O status que NÃO representa dinheiro vivo. Ver o cabeçalho (`draft` não entra). */
export const DEAD_CONTRACT_STATUSES: ReadonlySet<string> = new Set<ContractStatus>(['cancelled']);

/** `true` quando o recorte É exatamente o status morto (ver `includeDead`). */
export function isDeadContractStatusFilter(filter: ContractStatusFilter): boolean {
  return DEAD_CONTRACT_STATUSES.has(filter);
}

export interface ContractListSummary {
  /** Contratos no recorte — todos, inclusive os cancelados. */
  count: number;
  /** Soma dos que ainda representam dinheiro (fora os cancelados). */
  total: number;
  /** Quantos ficaram FORA da soma. Nunca cortado em silêncio (#61). */
  excludedCount: number;
}

export interface SummarizeContractsOptions {
  /**
   * Soma TAMBÉM os cancelados.
   *
   * Existe por causa do filtro explícito por "Cancelados": excluir todo mundo
   * ali faria o hero anunciar R$ 0,00 com a lista cheia embaixo — o número mais
   * visível da tela contradizendo o que ela mostra. A exclusão serve para a
   * faixa "Todos" não INFLAR o que está fechado; quando o usuário pede
   * justamente os cancelados, a pergunta mudou.
   */
  includeDead?: boolean;
}

/** Resumo do recorte JÁ FILTRADO (busca, status, período e valor aplicados). */
export function summarizeContractList(
  contracts: readonly ContractRow[],
  options: SummarizeContractsOptions = {},
): ContractListSummary {
  let total = 0;
  let excludedCount = 0;

  for (const contract of contracts) {
    if (!options.includeDead && DEAD_CONTRACT_STATUSES.has(contractStatusOf(contract))) {
      excludedCount += 1;
      continue;
    }
    total += totalOf(contract);
  }

  return { count: contracts.length, total, excludedCount };
}

/**
 * O RÓTULO do valor do hero — é ele que torna o número honesto.
 *
 * A soma sai do recorte já filtrado (busca + status + período + valor), então
 * ela muda enquanto o usuário digita. Sem dizer de qual recorte ela saiu,
 * "R$ 12.400,00" sob o título "Contratos" lê como "é isso que eu tenho fechado"
 * quando pode ser o recorte de rascunhos com uma busca ativa (#182/#188).
 */
export function contractListTotalLabel(filter: ContractStatusFilter): string {
  switch (filter) {
    case 'draft':
      return 'Total em rascunhos';
    case 'sent':
      return 'Total enviado';
    case 'partially_signed':
      return 'Total parcialmente assinado';
    case 'signed':
      return 'Total assinado';
    // O status morto ganha rótulo PRÓPRIO porque, sob o filtro dele, a soma
    // passa a incluí-lo (`includeDead`): dizer "Total em contratos" ali faria o
    // número parecer negócio fechado quando ele é justamente o oposto.
    case 'cancelled':
      return 'Total cancelado';
    default:
      return 'Total em contratos';
  }
}
